"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import {
  FaArrowRight,
  FaCheck,
  FaMagnifyingGlass,
  FaPenToSquare,
} from "react-icons/fa6";
import GradientButton from "@/components/ui/GradientButton";

const ease = [0.17, 0.85, 0.438, 0.99] as const;

const reportIssues = [
  {
    title: "Missing image alternative text",
    level: "Critical",
    dot: "bg-[#E11D48]",
    badgeBg: "bg-[#FDE8EA] dark:bg-rose-950/40",
    badgeText: "text-[#8A1C25] dark:text-rose-300",
  },
  {
    title: "Low colour contrast (4 instances)",
    level: "Serious",
    dot: "bg-[#F97316]",
    badgeBg: "bg-[#FEF0D9] dark:bg-amber-950/40",
    badgeText: "text-[#7A3A05] dark:text-amber-300",
  },
  {
    title: "Form input missing label",
    level: "Moderate",
    dot: "bg-[#F97316]",
    badgeBg: "bg-[#FEF6D8] dark:bg-yellow-950/40",
    badgeText: "text-[#7A5A05] dark:text-yellow-300",
  },
  {
    title: "Empty link text",
    level: "Moderate",
    dot: "bg-[#F5B83D]",
    badgeBg: "bg-[#FEF6D8] dark:bg-yellow-950/40",
    badgeText: "text-[#7A5A05] dark:text-yellow-300",
  },
  {
    title: "Improper heading structure",
    level: "Minor",
    dot: "bg-[#E11D48]",
    badgeBg: "bg-[#E6EEFF] dark:bg-blue-950/40",
    badgeText: "text-[#1E3A8A] dark:text-blue-300",
  },
];

const reportPages = [
  {
    name: "Home",
    score: 72,
    bar: "bg-[#F5B83D]",
    badgeBg: "bg-[#FEF0D9] dark:bg-amber-950/40",
    badgeText: "text-[#7A3A05] dark:text-amber-300",
  },
  {
    name: "About",
    score: 85,
    bar: "bg-[#2EB67D]",
    badgeBg: "bg-[#DDF5E9] dark:bg-emerald-950/40",
    badgeText: "text-[#0F6B44] dark:text-emerald-300",
  },
  {
    name: "Services",
    score: 78,
    bar: "bg-[#F5B83D]",
    badgeBg: "bg-[#FEF0D9] dark:bg-amber-950/40",
    badgeText: "text-[#7A3A05] dark:text-amber-300",
  },
  {
    name: "Contact",
    score: 65,
    bar: "bg-[#E11D48]",
    badgeBg: "bg-[#FDE8EA] dark:bg-rose-950/40",
    badgeText: "text-[#8A1C25] dark:text-rose-300",
  },
];

const reportDetails = [
  "Overall accessibility score",
  "Detailed issue list with screenshots",
  "Severity levels and priority recommendations",
  "Expert review and next steps",
];

const processSteps = [
  {
    title: "Submit Your Website",
    description: "Enter your website URL and business details.",
    icon: FaArrowRight,
  },
  {
    title: "We Run the Scan",
    description: "We review key pages for accessibility issues.",
    icon: FaMagnifyingGlass,
  },
  {
    title: "Receive Your Report",
    description: "Get clear findings and practical recommendations.",
    icon: FaCheck,
  },
  {
    title: "Fix & Improve",
    description: "Our team can help you implement the fixes.",
    icon: FaPenToSquare,
  },
];

export function AdaComplianceSampleReport() {
  const reduceMotion = useReducedMotion();
  const visualRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(visualRef, { once: true, amount: 0.2 });

  const [displayedScore, setDisplayedScore] = useState(0);
  const [displayedFound, setDisplayedFound] = useState(0);
  const [displayedCritical, setDisplayedCritical] = useState(0);
  const [displayedSerious, setDisplayedSerious] = useState(0);
  const [displayedModerate, setDisplayedModerate] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    if (reduceMotion) {
      setDisplayedScore(76);
      setDisplayedFound(28);
      setDisplayedCritical(6);
      setDisplayedSerious(12);
      setDisplayedModerate(10);
      return;
    }

    let frameId = 0;
    const startTime = performance.now();
    const duration = 1500;

    const updateCounts = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      setDisplayedScore(Math.round(easedProgress * 76));
      setDisplayedFound(Math.round(easedProgress * 28));
      setDisplayedCritical(Math.round(easedProgress * 6));
      setDisplayedSerious(Math.round(easedProgress * 12));
      setDisplayedModerate(Math.round(easedProgress * 10));

      if (progress < 1) {
        frameId = window.requestAnimationFrame(updateCounts);
      }
    };

    frameId = window.requestAnimationFrame(updateCounts);
    return () => window.cancelAnimationFrame(frameId);
  }, [isInView, reduceMotion]);

  const reveal = {
    initial: reduceMotion ? false : { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: { duration: reduceMotion ? 0 : 0.6, ease },
  };

  return (
    <motion.section
      {...reveal}
      className="overflow-hidden bg-linear-to-br from-amber-50 to-pink-50 px-4 py-12 sm:px-6 sm:py-16 lg:px-8 dark:from-gray-950 dark:to-gray-950"
    >
        <div className="mx-auto grid w-full max-w-7xl items-center gap-10 lg:grid-cols-2 lg:gap-14">
          <div
            ref={visualRef}
            aria-hidden="true"
            className="relative mx-auto w-full max-w-xl pb-20 sm:pb-24"
          >
            <div className="mx-auto w-11/12 rounded-3xl border border-slate-100/80 bg-white p-5 sm:p-6 pb-20 sm:pb-24 shadow-xl shadow-indigo-950/8 dark:border-slate-800/80 dark:bg-slate-900/95">
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-poppins text-sm sm:text-base font-bold text-indigo-950 dark:text-white">
                  Accessibility Overview
                </h3>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#137A50] dark:text-emerald-400">
                  <span className="relative flex size-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex size-2 rounded-full bg-[#2EB67D]" />
                  </span>
                  Live scan
                </span>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-6">
                <div className="flex flex-col items-center">
                  <div className="relative h-16 w-32">
                    <svg
                      viewBox="0 0 120 70"
                      className="absolute inset-0 h-16 w-32"
                      aria-hidden="true"
                    >
                      <defs>
                        <linearGradient
                          id="ada-report-gradient-2"
                          x1="0"
                          y1="0"
                          x2="1"
                          y2="0"
                        >
                          <stop offset="0%" stopColor="#2EB67D" />
                          <stop offset="60%" stopColor="#F5B83D" />
                          <stop offset="100%" stopColor="#F7845A" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M10 60a50 50 0 0 1 100 0"
                        fill="none"
                        stroke="#f1f2f7"
                        strokeWidth="10"
                        strokeLinecap="round"
                        className="dark:stroke-slate-800"
                      />
                      <motion.path
                        d="M10 60a50 50 0 0 1 100 0"
                        fill="none"
                        stroke="url(#ada-report-gradient-2)"
                        strokeWidth="10"
                        strokeLinecap="round"
                        initial={{ pathLength: 0 }}
                        animate={
                          isInView ? { pathLength: 0.76 } : { pathLength: 0 }
                        }
                        transition={{
                          duration: reduceMotion ? 0 : 1.5,
                          ease,
                        }}
                      />
                    </svg>
                    <span className="absolute inset-x-0 bottom-0 text-center font-poppins text-3xl font-extrabold leading-none text-indigo-950 dark:text-white">
                      {displayedScore}
                    </span>
                  </div>
                  <span className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
                    Overall score
                  </span>
                </div>

                <div className="grid flex-1 grid-cols-4 gap-2 text-center">
                  <div>
                    <p className="font-poppins text-2xl font-extrabold text-[#B4232F] dark:text-rose-400">
                      {displayedFound}
                    </p>
                    <p className="mt-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                      Issues Found
                    </p>
                  </div>
                  <div>
                    <p className="font-poppins text-2xl font-extrabold text-[#C42A33] dark:text-rose-400">
                      {displayedCritical}
                    </p>
                    <p className="mt-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                      Critical
                    </p>
                  </div>
                  <div>
                    <p className="font-poppins text-2xl font-extrabold text-[#A35E00] dark:text-amber-400">
                      {displayedSerious}
                    </p>
                    <p className="mt-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                      Serious
                    </p>
                  </div>
                  <div>
                    <p className="font-poppins text-2xl font-extrabold text-indigo-950 dark:text-white">
                      {displayedModerate}
                    </p>
                    <p className="mt-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                      Moderate
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <motion.div
              animate={reduceMotion ? {} : { y: [0, -10, 0] }}
              transition={{
                duration: 5.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute -bottom-4 sm:-bottom-5 left-0 sm:-left-3 z-20 w-3/5 rounded-2xl border border-slate-200/90 bg-white p-3.5 sm:p-4 shadow-2xl backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-900/98 dark:shadow-2xl"
            >
              <h4 className="font-poppins text-xs sm:text-sm font-bold text-indigo-950 dark:text-white">
                Top Issues
              </h4>
              <ul className="mt-2.5 space-y-2">
                {reportIssues.map((issue) => (
                  <li
                    key={issue.title}
                    className="flex items-center gap-2 text-xs font-medium text-indigo-950 dark:text-slate-200"
                  >
                    <span
                      className={`size-2 shrink-0 rounded-full ${issue.dot}`}
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {issue.title}
                    </span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${issue.badgeBg} ${issue.badgeText}`}
                    >
                      {issue.level}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              animate={reduceMotion ? {} : { y: [0, -8, 0], x: [0, -3, 0] }}
              transition={{
                duration: 6.8,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.7,
              }}
              className="absolute bottom-0 sm:-bottom-3 -right-1 sm:-right-3 z-30 w-1/2 rounded-2xl border border-slate-200/90 bg-white p-3.5 sm:p-4 shadow-2xl backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-900/98 dark:shadow-2xl"
            >
              <div className="flex flex-col">
                <h4 className="font-poppins text-xs sm:text-sm font-bold text-indigo-950 dark:text-white">
                  Sample Page Results
                </h4>
                <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                  https://yourwebsite.com
                </p>
              </div>
              <ul className="mt-2.5 space-y-2.5">
                {reportPages.map((page, idx) => (
                  <li key={page.name} className="flex flex-col gap-1">
                    <div className="flex items-center justify-between text-xs font-bold text-indigo-950 dark:text-slate-200">
                      <span>{page.name}</span>
                      <span
                        className={`rounded px-1.5 py-0.5 text-xs ${page.badgeBg} ${page.badgeText}`}
                      >
                        {page.score}
                      </span>
                    </div>
                    <div className="h-1 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                      <motion.div
                        className={`h-full rounded-full ${page.bar}`}
                        initial={{ width: "0%" }}
                        animate={
                          isInView
                            ? { width: `${page.score}%` }
                            : { width: "0%" }
                        }
                        transition={{
                          duration: reduceMotion ? 0 : 1.2,
                          delay: 0.2 + idx * 0.1,
                          ease,
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          <div className="flex flex-col items-start gap-5">
            <p className="font-poppins text-sm font-bold uppercase tracking-widest text-pink-700 dark:text-pink-400">
              Sample Report
            </p>
            <h2 className="font-poppins text-3xl font-bold leading-tight text-indigo-950 dark:text-white sm:text-4xl">
              Clear,{" "}
              <span className="text-violet-700 dark:text-violet-400">
                Actionable
              </span>{" "}
              Accessibility Reports
            </h2>
            <p className="max-w-2xl text-base leading-relaxed text-slate-600 dark:text-slate-300">
              Receive an easy-to-understand report with every issue identified,
              its severity, and step-by-step recommendations to fix it.
            </p>
            <ul className="grid gap-3 text-sm font-medium text-indigo-950 dark:text-slate-100 sm:text-base">
              {reportDetails.map((detail) => (
                <li key={detail} className="flex items-center gap-3">
                  <span className="grid size-6 shrink-0 place-items-center rounded-full bg-pink-600 text-white">
                    <FaCheck aria-hidden className="text-xs" />
                  </span>
                  {detail}
                </li>
              ))}
            </ul>
            {/* <GradientButton
              href="#ada-url"
              variant="sunset-pink"
              className="h-auto min-h-14 px-6 font-semibold"
            >
              Get Your Free Report <FaArrowRight aria-hidden />
            </GradientButton> */}
          </div>
        </div>
      </motion.section>
  );
}

export function AdaComplianceProcess() {
  const reduceMotion = useReducedMotion();
  const reveal = {
    initial: reduceMotion ? false : { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: { duration: reduceMotion ? 0 : 0.6, ease },
  };

  return (
    <motion.section
      {...reveal}
      className="bg-white px-4 py-14 sm:px-6 sm:py-20 lg:px-8 dark:bg-gray-950 transition-colors duration-300"
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-10">
        <div className="mx-auto flex max-w-2xl flex-col items-center gap-3 text-center">
          <p className="font-poppins text-sm font-bold uppercase tracking-widest text-pink-700 dark:text-pink-400">
            Our Process
          </p>
          <h2 className="font-poppins text-3xl font-bold text-indigo-950 dark:text-white sm:text-4xl">
            How It{" "}
            <span className="text-violet-700 dark:text-violet-400">Works</span>
          </h2>
          <p className="text-base text-slate-600 dark:text-slate-300">
            From scan to a more accessible website, we make it simple.
          </p>
        </div>
        <ol className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {processSteps.map(({ title, description, icon: Icon }, index) => (
            <li
              key={title}
              className="relative flex flex-col items-center gap-3 text-center"
            >
              <span className="grid size-16 place-items-center rounded-full bg-pink-50 text-pink-700 ring-8 ring-pink-50/60 dark:bg-pink-950/50 dark:text-pink-300 dark:ring-pink-950/30">
                <Icon aria-hidden className="text-xl" />
              </span>
              {index < processSteps.length - 1 && (
                <FaArrowRight
                  aria-hidden
                  className="absolute -right-5 top-4 hidden text-slate-300 lg:block"
                />
              )}
              <h3 className="font-poppins text-base font-bold text-indigo-950 dark:text-white">
                {index + 1}. {title}
              </h3>
              <p className="max-w-56 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                {description}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </motion.section>
  );
}

export default function AdaComplianceSections() {
  return (
    <>
      <AdaComplianceSampleReport />
      <AdaComplianceProcess />
    </>
  );
}

